import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import {
  ClientsStats,
  useClientCounts,
  ClientsTable,
  ClientsListSkeleton,
  ClientDetailDialog,
  type TypeFilter,
  CLIENTS_PER_PAGE,
} from "@/components/admin/clients";
import {
  MagnifyingGlass,
  ArrowCounterClockwise,
  Users,
} from "@phosphor-icons/react";
import type { Client, PaginatedClients } from "@/types";

export const Route = createFileRoute("/dashboard/admin/clients")({
  component: AdminClients,
});

const TAB_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Svi" },
  { value: "company", label: "Firmski" },
  { value: "independent", label: "Samostalni" },
];

function AdminClients() {
  const {
    page,
    setPage,
    searchQuery,
    debouncedSearch,
    setSearch: handleSearchChange,
    filters,
    setFilter,
    pageSize,
  } = usePagination<{ typeFilter: TypeFilter }>(
    { pageSize: CLIENTS_PER_PAGE },
    { typeFilter: "all" },
  );
  const { typeFilter } = filters;
  const [detailClient, setDetailClient] = useState<Client | null>(null);

  const counts = useClientCounts();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "clients", page, debouncedSearch, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        type: typeFilter,
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await fetch(`/api/clients?${params}`);
      if (!res.ok) throw new Error("Failed to fetch clients");
      return res.json() as Promise<PaginatedClients>;
    },
    placeholderData: (prev) => prev,
  });

  const clients = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Klijenti
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Registrovani klijenti i njihove terapijske sesije.
          </p>
        </div>

        <SegmentedFilter
          ariaLabel="Tip klijenta"
          options={TAB_OPTIONS}
          value={typeFilter}
          onChange={(type) => setFilter("typeFilter", type)}
        />
      </header>

      <ClientsStats counts={counts} />

      {/* Search */}
      <div className="flex items-center gap-1 overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm">
        <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
        <Input
          placeholder="Pretraži po imenu ili emailu..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="h-12 border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        {searchQuery && (
          <button
            onClick={() => handleSearchChange("")}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowCounterClockwise className="size-3" />
            Očisti
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading && clients.length === 0 ? (
        <ClientsListSkeleton />
      ) : clients.length === 0 ? (
        <EmptyState
          icon={Users}
          title={
            debouncedSearch ? "Nema rezultata pretrage" : "Nema klijenata"
          }
          description={
            debouncedSearch
              ? "Pokušajte s drugačijim pojmom za pretragu."
              : typeFilter === "company"
                ? "Nema klijenata povezanih s firmom."
                : typeFilter === "independent"
                  ? "Nema samostalnih klijenata."
                  : "Još nema registrovanih klijenata."
          }
          hasFilters={debouncedSearch.length > 0}
          onClear={() => handleSearchChange("")}
        />
      ) : (
        <>
          <ClientsTable clients={clients} onSelect={setDetailClient} />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {/* Detail dialog */}
      {detailClient && (
        <ClientDetailDialog
          client={detailClient}
          onClose={() => setDetailClient(null)}
        />
      )}
    </div>
  );
}
